import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { StreamModal } from '../components/StreamModal';
import { EventDetailModal } from '../components/EventDetailModal';
import { AdSense } from '../components/AdSense';

export function HomePage({ events = [], pages = [] }) {
  const [showModal, setShowModal] = useState(true);
  const [streams, setStreams] = useState([]);
  const [homeEvents, setHomeEvents] = useState(events);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState('');
  const [galleryVisibleCount, setGalleryVisibleCount] = useState(6);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);

  // Function to truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderGalleryMedia = (url, caption) => {
    const lower = url.toLowerCase();
    if (lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg')) {
      return (
        <video controls className="w-100" style={{ maxHeight: '260px', objectFit: 'cover' }}>
          <source src={url} type={`video/${lower.endsWith('.mp4') ? 'mp4' : lower.endsWith('.webm') ? 'webm' : 'ogg'}`} />
          Your browser does not support the video tag.
        </video>
      );
    }
    return <img src={url} alt={caption || 'Gallery media'} style={{ width: '100%', height: '260px', objectFit: 'cover' }} />;
  };

  const handleLike = async (id) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('app_session_token');
    if (!token) {
      alert('Please login to like gallery media.');
      return;
    }

    try {
      const res = await fetch(`/api/gallery/${id}/like`, {
        method: 'POST',
        headers: {
          'X-Session-Token': token,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (data.success) {
        setGalleryItems(prev => prev.map(item => item.id === id ? { ...item, likes_count: Number(item.likes_count) + 1 } : item));
      } else {
        alert(data.message || 'Unable to like this item.');
      }
    } catch (err) {
      console.error('Error liking gallery item:', err);
      alert('Unable to like the item right now.');
    }
  };

  const handleGalleryUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Please choose a photo or video to upload.');
      return;
    }

    const token = localStorage.getItem('session_token') || localStorage.getItem('app_session_token');
    if (!token) {
      alert('Please login to post to the gallery.');
      return;
    }

    setIsUploading(true);
    setUploadStatus('Uploading...');

    const formData = new FormData();
    formData.append('photo', uploadFile);
    formData.append('caption', uploadCaption);

    try {
      const res = await fetch('/api/gallery', {
        method: 'POST',
        headers: {
          'X-Session-Token': token
        },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setUploadStatus(data.pendingApproval ? 'Your post is pending admin approval.' : 'Your post is live in the gallery.');
        setUploadFile(null);
        setUploadCaption('');
        setGalleryVisibleCount(6);
        fetch('/api/gallery')
          .then(res => res.json())
          .then(data => {
            if (data.success && Array.isArray(data.photos)) {
              setGalleryItems(data.photos);
            }
          })
          .catch(() => {});
      } else {
        setUploadStatus(data.message || 'Upload failed.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('app_session_token');
    setIsLoggedIn(!!token);

    // Fetch events from API
    fetch('/api/events')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch events');
        return res.json();
      })
      .then(data => {
        console.log('Fetched events:', data);
        // API returns array directly
        if (Array.isArray(data)) {
          console.log('Events sorted by displayed_order:', data.map(e => ({ id: e.id, name: e.name, displayed_order: e.displayed_order })));
          setHomeEvents(data);
        } else if (data.events && Array.isArray(data.events)) {
          console.log('Events sorted by displayed_order:', data.events.map(e => ({ id: e.id, name: e.name, displayed_order: e.displayed_order })));
          setHomeEvents(data.events);
        }
      })
      .catch(err => console.error('Error fetching events:', err));

    // Fetch approved gallery media for homepage
    fetch('/api/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch gallery');
        return res.json();
      })
      .then(data => {
        if (data.success && Array.isArray(data.photos)) {
          setGalleryItems(data.photos);
        } else {
          setGalleryError('Could not load gallery items.');
        }
        setGalleryLoading(false);
      })
      .catch(err => {
        console.error('Error fetching gallery:', err);
        setGalleryError('Failed to load gallery items.');
        setGalleryLoading(false);
      });

    // Fetch all stream tabs from active_streams table
    fetch('/api/streams/tabs')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch stream tabs');
        return res.json();
      })
      .then(data => {
        console.log('Fetched stream tabs:', data);
        if (data.streams && Array.isArray(data.streams)) {
          setStreams(data.streams);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching stream tabs:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div>
      {!loading && <StreamModal streams={streams} isOpen={showModal} onClose={() => setShowModal(false)} />}
      <EventDetailModal event={selectedEvent} isOpen={showEventDetail} onClose={() => setShowEventDetail(false)} />
      <Navbar pages={pages} />
      <div className="hero-banner">
        <div className="container">
          <h1 className="display-4 fw-bold mb-4">🎉 Welcome to alo@laba Festival</h1>
          <p className="lead mb-4">
            Discover amazing events, register for parties, and have unforgettable experiences!
          </p>
          <a href="#upcoming-events" className="btn btn-light btn-lg">Browse Events</a>
        </div>
      </div>

      <div className="container py-5">
        {isLoggedIn && (
          <div className="card shadow-sm mb-5 border-0 rounded-4">
            <div className="card-body p-4">
              <h5 className="mb-3">Upload to Gallery</h5>
              {uploadStatus ? <div className="alert alert-info py-2 mb-3">{uploadStatus}</div> : null}
              <form onSubmit={handleGalleryUpload}>
                <div className="row g-3 align-items-center">
                  <div className="col-md-5">
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*,video/*"
                      onChange={e => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add a caption..."
                      value={uploadCaption}
                      onChange={e => setUploadCaption(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-primary w-100" disabled={isUploading}>
                      {isUploading ? 'Uploading...' : 'Post'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <section id="upcoming-events" className="mb-5">
          <h2 className="mb-4 text-center">🎊 Upcoming Events</h2>

          {homeEvents.length === 0 ? (
            <div className="alert alert-info text-center">
              <strong>No events yet!</strong> Check back soon for amazing parties.
            </div>
          ) : (
            <div className="row g-4">
              {homeEvents.slice(0, 6).map((event, index) => (
                <div 
                  key={event.id} 
                  className="col-12 col-sm-6 col-lg-4 animate-on-scroll is-visible"
                  style={{ cursor: 'pointer', transitionDelay: `${index * 100}ms` }}
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowEventDetail(true);
                  }}
                >
                  <div className="card h-100">
                    {event.image_url && (
                      <div style={{ height: '150px', overflow: 'hidden', borderTopLeftRadius: 'var(--bs-card-inner-border-radius)', borderTopRightRadius: 'var(--bs-card-inner-border-radius)' }}>
                        <img src={event.image_url} alt={event.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title m-0">{event.name}</h5>
                        <span className="badge badge-primary">#{event.id}</span>
                      </div>
                      <p className="card-text text-muted small">{truncateText(event.description, 150)}</p>
                      <div className="mb-3">
                        <p className="mb-1">
                          <strong>📅</strong> {event.date}
                        </p>
                        <p className="mb-1">
                          <strong>⏰</strong> {event.time}
                        </p>
                        <p className="mb-1">
                          <strong>📍</strong> {event.location}
                        </p>
                        <p className="mb-1">
                          <strong>👥</strong> {event.registrations}/{event.capacity}
                          {event.registrations < event.capacity ? (
                            <span className="badge bg-success ms-2">{event.capacity - event.registrations} left</span>
                          ) : (
                            <span className="badge bg-danger ms-2">Full</span>
                          )}
                        </p>
                        <p className="mb-1 text-success fw-bold">
                          <strong>🎟️</strong> {Number(event.price) > 0 ? `$${Number(event.price).toFixed(2)}` : 'Free'}
                        </p>
                      </div>
                      <span className="badge badge-success">Upcoming</span>
                    </div>
                    <div className="card-footer bg-white">
                      {event.allow_registrations ? (
                        <button className="btn btn-primary w-100 btn-sm">View Details</button>
                      ) : (
                        <button className="btn btn-secondary w-100 btn-sm" disabled>
                          Registrations Closed
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      <section id="gallery-preview" className="my-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">🎬 Festival Gallery</h2>
            <p className="text-muted mb-0">Approved photos and videos from the community.</p>
          </div>
          {isLoggedIn ? (
            <a href="/gallery" className="btn btn-outline-primary btn-sm">View Gallery</a>
          ) : (
            <a href="/gallery" className="btn btn-outline-primary btn-sm">Browse Gallery</a>
          )}
        </div>

        {galleryLoading ? (
          <div className="alert alert-info">Loading gallery...</div>
        ) : galleryError ? (
          <div className="alert alert-danger">{galleryError}</div>
        ) : galleryItems.length === 0 ? (
          <div className="alert alert-secondary">No gallery media available yet.</div>
        ) : (
          <>
            <div className="row g-4">
              {galleryItems.slice(0, galleryVisibleCount).map(item => (
                <div key={item.id} className="col-12 col-sm-6 col-lg-4">
                  <div className="card h-100 shadow-sm rounded-4 overflow-hidden">
                    {renderGalleryMedia(item.image_url, item.caption)}
                    <div className="card-body">
                      <p className="card-text mb-3">{item.caption || 'No caption'}</p>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</small>
                        <div className="d-flex align-items-center gap-2">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleLike(item.id)}>
                            ❤️ Like
                          </button>
                          <span className="badge bg-primary">{item.likes_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {galleryVisibleCount < galleryItems.length && (
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setGalleryVisibleCount(prev => Math.min(prev + 6, galleryItems.length))}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <AdSense />

      <hr />

      <section className="py-5">
          <h3 className="mb-5 text-center">📢 Why Choose Us?</h3>
          <div className="row g-4">
            <div className="col-12 col-md-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h1 className="mb-3">🎯</h1>
                  <h5 className="card-title">Amazing Events</h5>
                  <p className="card-text">Carefully curated parties for everyone</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h1 className="mb-3">🎵</h1>
                  <h5 className="card-title">Great Music</h5>
                  <p className="card-text">DJ mixes and live entertainment</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h1 className="mb-3">🍽️</h1>
                  <h5 className="card-title">Good Food</h5>
                  <p className="card-text">Delicious snacks and refreshments</p>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h1 className="mb-3">👥</h1>
                  <h5 className="card-title">New Friends</h5>
                  <p className="card-text">Meet people with shared interests</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Ad Section After Why Choose Us */}
      <div style={{ backgroundColor: '#f8f9fa', padding: '30px 0' }}>
        <div className="container">
          <AdSense variant="fluid" />
        </div>
      </div>

      <div className="container py-5">
        {/* Additional ad space */}
        <div style={{ marginBottom: '40px' }}>
          <AdSense variant="responsive" />
        </div>
      </div>

      <Footer />
    </div>
  );
}
